// Module 11 — controls. Drive the sim ONLY through the state store: domain Commands via
// dispatchCommand (setClock/setScenario/setLayerVisible/toggleNode/injectFault), and the transport
// pass-throughs setRunning/setSpeed. play/pause/speed are worker-lifecycle, NOT members of the
// Command union (dashboards.v1 invariant). A scenario preset runs its full command bundle.
import type { Command, Layer, Season, DayType } from "../domain/index";
import type { Store, UiState } from "../state/index";
import type { ScenarioCatalog } from "./seam";

// ── pure Command builders ────────────────────────────────────────────────────────
export function hourCommand(hour: number, speed: number): Command {
  return { t: "setClock", hour, speed };
}
export function scenarioCommand(scenarioId: string): Command {
  return { t: "setScenario", scenarioId };
}
export function layerCommand(layer: Layer, visible: boolean): Command {
  return { t: "setLayerVisible", layer, visible };
}
// v3: live environment overrides — the season/temperature/dayType controls (hour rides setClock)
export function envCommand(env: { season?: Season; dayType?: DayType; tempC?: number }): Command {
  return { t: "setEnv", ...env };
}

export interface Controls {
  setHour(hour: number): void;
  setScenario(scenarioId: string): void;
  setLayer(layer: Layer, visible: boolean): void;
  setSeason(season: Season): void;
  setDayType(dayType: DayType): void;
  setTempC(tempC: number): void;
  play(): void;
  pause(): void;
  setSpeed(speed: number): void;
  runPreset(presetId: string): void;
  // Phase 1.1: voltage overlay — a VIZ-LOCAL coloring mode (not a simulation layer). Optional: the
  // composition root injects the viz callback when available; the control panel gates the button on it.
  setVoltageOverlay?(on: boolean): void;
  readonly voltageOverlay?: boolean;
}

export function makeControls(store: Store<UiState>, catalog: ScenarioCatalog): Controls {
  return {
    setHour(hour) {
      const speed = store.getState().clock.speed; // current transport speed is preserved
      store.getState().dispatchCommand(hourCommand(hour, speed));
    },
    setScenario(scenarioId) {
      store.getState().dispatchCommand(scenarioCommand(scenarioId));
    },
    setLayer(layer, visible) {
      store.getState().dispatchCommand(layerCommand(layer, visible));
    },
    setSeason(season) {
      store.getState().dispatchCommand(envCommand({ season }));
    },
    setDayType(dayType) {
      store.getState().dispatchCommand(envCommand({ dayType }));
    },
    setTempC(tempC) {
      store.getState().dispatchCommand(envCommand({ tempC }));
    },
    play() {
      store.getState().setRunning(true);
    },
    pause() {
      store.getState().setRunning(false);
    },
    setSpeed(speed) {
      store.getState().setSpeed(speed);
    },
    runPreset(presetId) {
      const preset = catalog.presets.find((p) => p.id === presetId);
      if (!preset) return;
      const dispatch = store.getState().dispatchCommand;
      for (const cmd of preset.commands) dispatch(cmd);
    },
  };
}
